/**
 * 政府端 — 车辆详情（轨迹回放 + 告警 + 基本信息）
 */
import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { fetchOrder, fetchTrack, fetchOrderAlerts, fetchFences } from '@/api/transit';
import TrackMap from '@/components/common/TrackMap';
import { AlertLevelTag, Empty, Loading, OrderStatusTag } from '@/components/common';
import type { GPSPoint, TransitAlert, TransitFence, TransitOrder } from '@/types';
import { alertTypeText, fmtTime } from '@/utils/format';

export default function GovVehicleDetail() {
  const { id } = useParams<{ id: string }>();
  const transitId = Number(id ?? 0);

  const [order, setOrder] = useState<TransitOrder | null>(null);
  const [points, setPoints] = useState<GPSPoint[]>([]);
  const [alerts, setAlerts] = useState<TransitAlert[]>([]);
  const [fences, setFences] = useState<TransitFence[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!transitId) return;
    Promise.all([fetchOrder(transitId), fetchTrack(transitId), fetchOrderAlerts(transitId), fetchFences(transitId)]).then(
      ([o, t, a, f]) => {
        setOrder(o);
        setPoints(t);
        setAlerts(a.list);
        setFences(f);
        setLoading(false);
      },
    );
  }, [transitId]);

  if (loading) return <Loading />;
  if (!order) return <Empty text="运输单不存在" />;

  return (
    <div>
      <Link to="/gov/vehicles" style={{ fontSize: 13 }}>
        ← 返回车辆列表
      </Link>
      <div className="row-between" style={{ marginTop: 8, marginBottom: 16 }}>
        <div>
          <div className="page-title">
            {order.vehicle?.plate_no ?? `车辆 #${order.vehicle_id}`}
            <span style={{ fontSize: 14, marginLeft: 12, color: 'var(--color-ink-secondary)' }}>{order.transit_no}</span>
          </div>
          <div className="page-sub" style={{ marginBottom: 0 }}>
            {order.origin_city} → {order.destination_city} · 司机 {order.driver?.name ?? '—'}
          </div>
        </div>
        <OrderStatusTag status={order.status} />
      </div>

      <div className="grid grid-3" style={{ marginBottom: 16 }}>
        <div className="card">
          <div className="card-title">运输信息</div>
          <div style={{ fontSize: 13, lineHeight: 2 }}>
            <div>牛只数量：{order.cattle_count} 头</div>
            <div>起运时间：{fmtTime(order.actual_depart_time)}</div>
            <div>到达时间：{fmtTime(order.actual_arrive_time)}</div>
            <div>检疫状态：{order.quarantine_status === 1 ? '✅ 通过' : order.quarantine_status === 3 ? '⏳ 待核验' : '❌ 不通过'}</div>
          </div>
        </div>
        <div className="card">
          <div className="card-title">最新位置</div>
          <div style={{ fontSize: 13, lineHeight: 2 }}>
            <div style={{ fontFamily: 'monospace' }}>
              经度 {order.last_lng.toFixed(6)}
              <br />
              纬度 {order.last_lat.toFixed(6)}
            </div>
            <div>速度 {order.last_speed.toFixed(1)} km/h</div>
            <div>上报时间 {fmtTime(order.last_gps_time)}</div>
          </div>
        </div>
        <div className="card">
          <div className="card-title">司机信息</div>
          <div style={{ fontSize: 13, lineHeight: 2 }}>
            <div>姓名：{order.driver?.name ?? '—'}</div>
            <div>手机：{order.driver?.phone ?? '—'}</div>
            <div>驾驶证：{order.driver?.license_no ? order.driver.license_no.slice(0, 6) + '****' : '—'}</div>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title">
          <span>🗺 轨迹回放（{points.length} 个点）</span>
          <span style={{ fontSize: 12, color: 'var(--color-ink-muted)' }}>SVG 静态示意 · 生产接高德 GCJ-02</span>
        </div>
        <TrackMap
          points={points}
          fences={fences.map((f) => ({ lng: f.center_lng, lat: f.center_lat, radius_m: f.radius_m, fence_type: f.fence_type, fence_name: f.fence_name }))}
        />
      </div>

      <div className="card">
        <div className="card-title">⚠️ 告警记录（{alerts.length}）</div>
        {alerts.length === 0 ? (
          <Empty text="暂无告警" />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>类型</th>
                <th>级别</th>
                <th>内容</th>
                <th>时间</th>
              </tr>
            </thead>
            <tbody>
              {alerts.map((a) => (
                <tr key={a.id}>
                  <td>{alertTypeText(a.alert_type)}</td>
                  <td>
                    <AlertLevelTag level={a.alert_level} />
                  </td>
                  <td style={{ maxWidth: 360 }}>{a.alert_content}</td>
                  <td style={{ fontSize: 12, color: 'var(--color-ink-secondary)' }}>{fmtTime(a.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
